import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { GameOption } from "entities/game/types";
import { useMetadataTagsListQuery } from "entities/metadataTags/queries/useMetadataTagsListQuery";
import { gamesOrderTypeOptions } from "entities/modules/constants";
import { useSelectedGamesData } from "entities/modules/stores/useSelectedGamesData";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import { GamesOrderType, SelectedGame } from "entities/modules/types";
import { OrderedStudio } from "entities/studios/types";
import { useThemePrimaryTagsQuery } from "entities/themeTagTypes/queries/useThemePrimaryTagsQuery";
import { FC, useCallback, useMemo } from "react";
import AddManualGamesField from "shared/components/AddManualGamesField";
import ContentSection from "shared/components/Drawer/components/ContentSection";
import Select from "shared/components/Form/Select";
import ManualSelectedStudioGamesList from "shared/components/ManualSelectedStudioGamesList";
import RandomSelectedStudioGamesList from "shared/components/RandomSelectedStudioGamesList";
import { COLOR_BLACK } from "shared/theme/colors";
import { formatTagsWithTypes } from "widgets/ModuleTagsWidget/utils";
import { useContentScheduleModuleDetails } from "../../../../../../../context";

type Props = {
  studio: OrderedStudio;
};

// TODO: This component is almost copy/paste from SelectedGames component
// TODO: Refactor to reduce duplications
const SelectedStudioGames: FC<Props> = ({ studio }) => {
  const { isLoadingSelectedGamesList, availableStudios } =
    useContentScheduleModuleDetails();

  const { tagBlocks } = useTagBlocksStore();
  const { data: metadataTags } = useMetadataTagsListQuery();
  const { data: themePrimaryTags } = useThemePrimaryTagsQuery();

  const {
    selectedGames,
    orderType,
    manualSelectedGames,
    tagSelectedGames,
    setOrderType,
    setSelectedGames,
    setManualSelectedGames,
  } = useSelectedGamesData();

  const studioGames = selectedGames.filter(
    (game) => String(game.studioId) === studio.id,
  );

  const hasRandomOrder = orderType === GamesOrderType.Random;

  const currentStudioSelectedGames = useMemo(() => {
    return manualSelectedGames.filter(
      (game) => game.studioId?.toString() === studio.id?.toString(),
    );
  }, [manualSelectedGames, studio.id]);

  const handleChange = (value: string) => {
    setOrderType(value as GamesOrderType);
  };

  const formatSelectedGamesHints = useCallback(
    (game: GameOption) => {
      return formatTagsWithTypes({
        tagBlocks,
        option: game,
        themePrimaryTags,
        metadataTags,
      });
    },
    [tagBlocks, themePrimaryTags, metadataTags],
  );

  const gamesFromOtherStudios = useMemo(
    () =>
      manualSelectedGames.filter(
        (game) => game.studioId?.toString() !== studio.id?.toString(),
      ),
    [manualSelectedGames, studio.id],
  );

  const tagSelectedGameIds = useMemo(() => {
    return tagSelectedGames.map((game) => game.id);
  }, [tagSelectedGames]);

  const handleSelectedGamesManuallyChange = (games: SelectedGame[]) => {
    setManualSelectedGames([...gamesFromOtherStudios, ...games]);
  };

  return (
    <Stack spacing="1rem">
      <ContentSection title="GAMES">
        <Select
          label="Games Order"
          labelColor={COLOR_BLACK}
          placeholder="Select an order"
          disabledPlaceholder
          fullWidth
          required
          value={orderType}
          handleChange={handleChange}
          options={gamesOrderTypeOptions}
        />
        <Box marginTop="1rem">
          <AddManualGamesField
            selectedGamesManually={currentStudioSelectedGames}
            setSelectedGamesManually={handleSelectedGamesManuallyChange}
            formatSelectedGamesHints={formatSelectedGamesHints}
            tagSelectedGameIds={tagSelectedGameIds}
            availableStudios={availableStudios || []}
            studioId={studio.id}
          />
        </Box>
        {hasRandomOrder && (
          <RandomSelectedStudioGamesList
            games={studioGames}
            selectedGames={selectedGames}
            setSelectedGames={setSelectedGames}
          />
        )}
        {!hasRandomOrder && (
          <ManualSelectedStudioGamesList
            games={studioGames}
            isLoadingSelectedGamesList={isLoadingSelectedGamesList}
            studio={studio}
            selectedGames={selectedGames}
            setSelectedGames={setSelectedGames}
          />
        )}
      </ContentSection>
    </Stack>
  );
};

export default SelectedStudioGames;
